import React, { PureComponent } from 'react'

import { DEFAULT_SITE_REF_ID } from '../../../constants';
import { createFormBag, FormBag, FormData, FormErrors, FormUpdateEvent, validateFormBag } from 'react-formage'
import { Button, ButtonGroup } from 'components/ui/button'
import { FieldSection } from 'components/ui/fieldset'
import { Input } from 'components/ui/input'
import { SelectRef } from 'components/ui/select'
import { Modal } from 'components/ui/modal'
import { StatusGuard } from 'components/ui/status-guard'

import { sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { Site } from 'generated/mos/structure'
import { Mood } from 'generated/mos/org/getty/audio'
import { CreateMoodRequest } from 'generated/mos/org/getty/audiomanagement'

import { pick } from 'helpers/core'
import { Status } from 'helpers/status'

import { actionCreators } from '../../../actions'
import { GettyStore } from '../../../store'
import { moodSchema } from '../../../schemas/mood'

type ConnectedProps = {
  moodCreate: GettyStore['getty']['moodCreate'];
  moodUpdate: GettyStore['getty']['moodUpdate'];
  sites: GettyStore['getty']['sites'];
};

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly projectId: string;
}

type ActionProps =
  Pick<typeof actionCreators, 'gettyCreateMoodRequest'> &
  Pick<typeof actionCreators, 'gettyListSitesRequest'> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type Props = ConnectedProps & DirectProps & ActionProps;

type FormValues = {
  readonly moodName: string;
  readonly siteRef: Site.Ref | undefined;
  readonly description: string;
};

type State  = {
  readonly bag: FormBag<FormValues>;
}

class MoodCreateView extends PureComponent<Props, State> {
  public constructor(props: Props) {
    super(props)
    this.state = {
      bag: createFormBag({
        moodName: '',
        siteRef: undefined,
        description: '',
      }),
    }
  }

  public componentDidMount() {
    this.props.gettyListSitesRequest()
  }

  public componentDidUpdate() {
    const {
      historyPush,
      onClose,
      projectId,
      toastNotification,
      moodCreate,
      moodUpdate,
      sites,
    } = this.props

    if (moodCreate.status === Status.Failed) {
      toastNotification({ type: 'error', text: 'There was a problem creating the mood journey.' })
    }

    if (
      moodCreate.status === Status.Complete &&
      moodUpdate.status === Status.Stale &&
      moodUpdate.data.mood
    ) {
      toastNotification({ type: 'success', text: 'Your mood journey has been created.' })
      onClose()
      const moodId = Mood.mustRef(moodUpdate.data.mood.ref).id
      historyPush(`/project/${projectId}/mood/${moodId}`)
    }

    if(sites.status === Status.Ready && !this.state.bag.values.siteRef && sites.data.sites) {
      const defaultSite = sites.data.sites.filter(site => Site.mustRef(site.ref).id === DEFAULT_SITE_REF_ID)[0]
      this.setState({
        bag: {
          ...this.state.bag,
          values: {
            ...this.state.bag.values,
            siteRef: defaultSite ? Site.mustRef(defaultSite.ref) : undefined
          },
        }
      })
    }
  }

  private onClose = () => {
    this.props.onClose()
  }

  private onFormUpdate = (event: FormUpdateEvent<FormValues>) => {
    this.setState({
      bag: event.bag,
    })
  }

  private onFormValidate = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}

    if (!values.moodName || !values.moodName.trim().length) {
      errors.moodName = 'Mood journey name required';
    }
    return errors
  }

  private save = () => {
    const bag = validateFormBag(this.state.bag, this.onFormValidate)
    this.setState({ bag })

    if (!bag.valid) {
      return
    }

    const { moodName: name, description, siteRef } = bag.values

    const payload: CreateMoodRequest.Entity = {
      ...CreateMoodRequest.defaults,
      name: moodSchema.name ? moodSchema.name.write(name) : name,
      description: moodSchema.description ? moodSchema.description.write(description) : description,
      siteRef,
    }
    this.props.gettyCreateMoodRequest(payload)
  }

  public render() {
    const { moodCreate, sites } = this.props
    const { bag: { errors, touched, valid } } = this.state
    const loading = moodCreate.status === Status.Loading

    const siteOpts = sites.status === Status.Ready && sites.data.sites
      ? sites.data.sites.map((site: Site.Entity) => ({
        label: site.name,
        value: Site.mustRef(site.ref),
      }))
      : []

    const footer = (
      <ButtonGroup>
        <Button
          type="button"
          key="cancel"
          appearance="secondary"
          onClick={(event) => { event.preventDefault(); this.onClose(); }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isDisabled={loading || !valid}
          onClick={() => this.save()}
        >
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </ButtonGroup>
    )

    return (
      <Modal onClose={this.onClose} header={<h3>Create mood journey</h3>} footer={footer}>
        <form noValidate onSubmit={(event) => { event.preventDefault(); this.save(); }}>
          <StatusGuard status={{ status: Status.Idle }} mode="form">
            <FormData
              bag={this.state.bag}
              onUpdate={this.onFormUpdate}
              validate={this.onFormValidate}
            >
              <Input
                label="Mood journey name"
                field="moodName"
                error={touched.moodName ? errors.moodName : undefined}
              />
              <SelectRef<FormValues, 'siteRef'>
                label="Site"
                field="siteRef"
                options={siteOpts}
                error={touched.siteRef ? errors.siteRef : undefined}
              />
              <FieldSection />
              <Input
                label="Description"
                field="description"
                component="textarea"
              />
            </FormData>
          </StatusGuard>
        </form>
      </Modal>
    )
  }
}

export const MoodCreate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore>(
  ({ getty }) => pick(getty, 'moodCreate', 'moodUpdate', 'sites'),
  {
    ...pick(actionCreators, 'gettyCreateMoodRequest', 'gettyListSitesRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(MoodCreateView)
